import ko from "knockout";
import register from "flow/knockout/register";

function formatWithComma(x: string, precision?: number, seperator?: string) {
    const options = {
        precision: precision || 2,
        seperator: seperator || ","
    };
    if (x === undefined || x === null || x === "") {
        x = "0";
    }

    if (typeof x === "string") {
        x = x.replace(/\s/gi, "");
    }

    const number = parseFloat(x);

    if (isNaN(number) || number === 0) {
        return "";
    }

    let formatted = number.toFixed(options.precision);

    const regex = new RegExp("^(-?\\d+)[^\\d](\\d{" + options.precision + "})$");
    formatted = formatted.replace(regex, "$1" + options.seperator + "$2");
    return formatted;
}

function reverseFormat(x: string) {
    if (x === undefined || x === null || x === "") {
        x = "0";
    }
    const regex = new RegExp("^(\\d+)[^\\d](\\d+)$");
    const formatted = x.replace(regex, "$1.$2");
    const parsedValue = parseFloat(formatted);
    return isNaN(parsedValue) ? 0 : parsedValue;
}

function allowOnlyNumeric(allowNegative: boolean, event: KeyboardEvent) {
    // Allow: backspace, delete, tab, escape, and enter
    if (
        event.keyCode == 46 ||
        event.keyCode == 8 ||
        event.keyCode == 9 ||
        event.keyCode == 27 ||
        event.keyCode == 13 ||
        // Allow: Ctrl+A
        (event.keyCode == 65 && event.ctrlKey === true) ||
        // Allow: . ,
        event.keyCode == 188 ||
        event.keyCode == 190 ||
        event.keyCode == 110 ||
        // Allow subtract and dash (negative numbers)
        (event.keyCode == 109 && allowNegative) ||
        (event.keyCode == 189 && allowNegative) ||
        // Allow: home, end, left, right
        (event.keyCode >= 35 && event.keyCode <= 39)
    ) {
        // let it happen, don't do anything
        return;
    } else {
        // Ensure that it is a number and stop the keypress
        if (
            event.shiftKey ||
            ((event.keyCode < 48 || event.keyCode > 57) && (event.keyCode < 96 || event.keyCode > 105))
        ) {
            event.preventDefault();
        }
    }
}

/*

    Explanation of the RegExp used above:

    ^(\\d+)[^\\d](\\d{' + options.precision + '})$$

        ^        = pattern must match from the start of the string

        (\\d+)   = 1 or more digits
                   - the brackets capture the matched string
                     to be used in the replace function as $1

        [^\\d]   = any character but a digit
                   - this includes letters, whitespace and
                     punctutation (any character but 0-9)

        (\\d{X}) = X number of digits (above we use options.precision)
                   - the brackets capture the matched string
                     to be used in the replace function as $2

        $        = pattern must match to the end of the string
                   - here we've used ^...$ so the pattern must
                     match the entire string
    */

register.registerKnockoutBinding("commaDecimalSpaceIfZeroFormatterV2", {
    init: function (element, valueAccessor, allBindingsAccessor) {
        let observable = valueAccessor();
        const allowNegative = !!allBindingsAccessor.get("commaDecimalAllowNegative");

        const boundFunction = allowOnlyNumeric.bind(null, allowNegative);

        element.addEventListener("keydown", boundFunction);
        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            element.removeEventListener("keydown", boundFunction);
        });

        const interceptor = ko
            .computed({
                read: function () {
                    const value = ko.isObservable(observable) ? observable() : observable;
                    return formatWithComma(value);
                },
                write: function (newValue) {
                    if (ko.isObservable(observable)) {
                        const oldValue = observable();
                        const valueToWrite = reverseFormat(newValue);

                        //only write if it changed
                        if (valueToWrite !== oldValue) {
                            observable(valueToWrite);
                        } else {
                            //if the rounded value is the same, but a different value was written, force a notification for the current field
                            if (newValue !== oldValue) {
                                observable.notifySubscribers(valueToWrite);
                            }
                        }
                    } else {
                        observable = reverseFormat(newValue);
                    }
                }
            })
            .extend({ notify: "always" });

        if (element.tagName == "INPUT")
            ko.applyBindingsToNode(element, {
                value: interceptor
            });
        else
            ko.applyBindingsToNode(element, {
                text: interceptor
            });
    }
});

(ko.validation as any).makeBindingHandlerValidatable("commaDecimalSpaceIfZeroFormatterV2");
